import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { sessionActions } from '../../../store';
import { EVV_STATUS_POLL_INTERVAL_MS, EVV_TOKEN_STATUSES } from '../constants';
import { buildEvvVerificationRoute, createEvvToken, isEvvSideCaptured } from '../utils';

import type { IEVVSessionStatus } from '../../../utils/types';
import type { UseEvvQrDialogPollParams } from './types';

export function useEvvQrDialogPoll({
  dialog,
  onStatus,
  onTokenRefreshed,
  onCaptured,
}: UseEvvQrDialogPollParams) {
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const callbacksRef = useRef({ onStatus, onTokenRefreshed, onCaptured });
  const kind = dialog?.kind;
  const sessionId = dialog?.sessionId;

  useEffect(() => {
    callbacksRef.current = { onStatus, onTokenRefreshed, onCaptured };
  });

  // Keyed on the dialog side only, so a token refresh that changes the route keeps the same poll.
  useEffect(() => {
    if (!kind || sessionId == null) return;

    let cancelled = false;
    let refreshing = false;
    let lastRefreshAt = 0;

    const refreshToken = async () => {
      refreshing = true;
      const response = await dispatch(
        createEvvToken({ kind, sessionId, replaceActive: true, getAccessTokenSilently }),
      );
      refreshing = false;
      lastRefreshAt = Date.now();
      // A failed refresh stays silent; the next poll still sees the expired token and retries.
      if (cancelled || response.error) return;

      callbacksRef.current.onTokenRefreshed(
        buildEvvVerificationRoute({ token: response.payload.token, kind, sessionId }),
      );
    };

    const pollEvvStatus = async () => {
      const polledAt = Date.now();
      const response = await dispatch(
        sessionActions.getEvvStatus({ sessionId, getAccessTokenSilently }),
      );
      if (cancelled || response.error) return;

      const nextStatus: IEVVSessionStatus = response.payload;
      callbacksRef.current.onStatus?.(nextStatus);
      if (isEvvSideCaptured(nextStatus[kind])) {
        cancelled = true;
        callbacksRef.current.onCaptured();
        return;
      }

      // A poll sent before the last refresh finished can still report the replaced token as expired.
      if (
        nextStatus[kind]?.token_status === EVV_TOKEN_STATUSES.EXPIRED &&
        !refreshing &&
        polledAt > lastRefreshAt
      ) {
        void refreshToken();
      }
    };

    void pollEvvStatus();
    const intervalId = window.setInterval(pollEvvStatus, EVV_STATUS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [dispatch, getAccessTokenSilently, kind, sessionId]);
}
